import React, { useState } from "react";
import { useParams, Link, useNavigate, Navigate } from "react-router-dom";
import {
  ArrowRight, Check, MessageCircle, ShieldCheck, Sparkles, Layers, Camera, ChevronDown, Star,
  Phone, MapPin, PackageCheck, Truck, Wrench, Home as HomeIcon, Heart, Leaf, Tag, Box, ClipboardList,
  FileText, Users, Clock, TrendingUp,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { SERVICES, HIDDEN_SERVICE_KEYS, SITE_URL, buildServiceLd, buildBreadcrumbLd, buildFaqLd } from "@/lib/seo";
import { indexableCitiesForService } from "@/lib/localPages";
import { SERVICE_IMG, REASSURANCE_IMG, TRUST_BAR, TRUST_BAR_I18N, HERO_BADGES_I18N, SERVICE_CONTENT, SERVICE_LABELS, SERVICE_PAGE_UI, SERVICE_I18N, SERVICE_NAMES_I18N } from "@/lib/serviceContent";
import { SERVICE_DEEP_I18N } from "@/lib/serviceContent_i18n_deep";
import { PRICING_FACTORS, COMMITMENTS_I18N, FAQ_3A, PHASE3A_UI } from "@/lib/servicePhase3A";
import { useI18n } from "@/lib/i18n";
import { useExtraT } from "@/lib/extraI18n";

// ---------- ICON MAP ---------------------------------------------------------
const ICONS = {
  shield: ShieldCheck, box: Box, truck: Truck, tool: Wrench, home: HomeIcon, sparkles: Sparkles,
  heart: Heart, leaf: Leaf, tag: Tag, layers: Layers, clean: Sparkles, check: Check,
  file: FileText, users: Users, map: MapPin, clock: Clock,
};

const CANTONS = [
  { slug: "vaud", name: "Vaud" }, { slug: "geneve", name: "Genève" }, { slug: "fribourg", name: "Fribourg" },
  { slug: "neuchatel", name: "Neuchâtel" }, { slug: "valais", name: "Valais" }, { slug: "jura", name: "Jura" },
];

// =================================================================================
// PAGE
// =================================================================================
export default function ServicePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang } = useI18n();
  const x = useExtraT();
  const service = SERVICES.find((s) => s.slug === slug);
  const [openIdx, setOpenIdx] = useState(0);
  const [zip, setZip] = useState("");
  const [zipFeedback, setZipFeedback] = useState(null);

  if (!service) return <Navigate to="/" replace />;
  // Service « Home Organizing » retiré de l'offre : on redirige vers la liste des services.
  if (service.key === "organize") return <Navigate to="/services" replace />;

  // Merge per-service FR content with DE/EN overrides for badge/promise/heroAlt/reassuranceTitle.
  const baseContent = SERVICE_CONTENT[service.key] || SERVICE_CONTENT.move;
  const i18nOverride = lang !== "fr" ? (SERVICE_I18N[service.key]?.[lang] || {}) : {};
  const deepOverride = lang !== "fr" ? (SERVICE_DEEP_I18N[service.key]?.[lang] || {}) : {};
  // Deep merge: arrays (includes, tiers, testimonials, faq, reassuranceItems, caseStudy.bullets) come from deepOverride.
  // For testimonials we keep FR `name`, `city`, `rating`, `source` and only replace `text` + `type`.
  let testimonials = baseContent.testimonials;
  if (deepOverride.testimonials) {
    testimonials = baseContent.testimonials.map((t, i) => ({ ...t, ...(deepOverride.testimonials[i] || {}) }));
  }
  const content = {
    ...baseContent,
    ...i18nOverride,
    ...deepOverride,
    testimonials,
    caseStudy: { ...(baseContent.caseStudy || {}), ...(deepOverride.caseStudy || {}) },
  };
  const u = SERVICE_PAGE_UI[lang] || SERVICE_PAGE_UI.fr;
  const p3a = PHASE3A_UI[lang] || PHASE3A_UI.fr;
  const pricingFactors = (PRICING_FACTORS[service.key]?.[lang]) || PRICING_FACTORS[service.key]?.fr || [];
  const commitments = COMMITMENTS_I18N[lang] || COMMITMENTS_I18N.fr;
  const serviceName = SERVICE_NAMES_I18N[lang]?.[service.key] || service.name;
  const heroImg = SERVICE_IMG[service.key];
  // Phase 3A FAQ: exactly 5 unique questions per service, fully translated.
  const faqs = (FAQ_3A[service.key]?.[lang]) || FAQ_3A[service.key]?.fr || [];
  const breadcrumb = [
    { name: "Accueil", url: `${SITE_URL}/` },
    { name: "Services", url: `${SITE_URL}/services` },
    { name: service.name, url: `${SITE_URL}/services/${service.slug}` },
  ];
  const jsonLd = [buildServiceLd(service), buildBreadcrumbLd(breadcrumb), buildFaqLd(faqs)];
  const whatsappHref = `https://wa.me/41782321850?text=${encodeURIComponent(`Bonjour Mopper, je vous envoie des photos pour un devis ${service.name}`)}`;
  const localCities = indexableCitiesForService(service.slug);
  const tarifSlug = { demenagement: "demenagement", debarras: "debarras", nettoyage: "nettoyage-fin-de-bail" }[service.slug];

  function checkZip(e) {
    e.preventDefault();
    const v = zip.trim();
    if (!/^\d{4}$/.test(v)) {
      setZipFeedback({ ok: false, msg: x.sp.zonesInvalid });
      return;
    }
    const first = parseInt(v[0], 10);
    const ok = first >= 1 && first <= 3; // 1xxx VD/GE/FR/NE/JU/VS, 2xxx, 3xxx Berne/VS
    setZipFeedback(ok
      ? { ok: true, msg: x.sp.zonesOk(v) }
      : { ok: false, msg: x.sp.zonesKo(v) });
  }

  return (
    <div className="min-h-screen bg-white text-[#0A0F2C]" data-testid={`service-page-${service.slug}`}>
      <Seo title={service.metaTitle} description={service.metaDescription} path={`/services/${service.slug}`} jsonLd={jsonLd} />
      <Header />

      {/* ============== 1. HERO ============== */}
      <section className="relative bg-[#2D5BFF] hero-noise overflow-hidden pt-28 md:pt-36 pb-12 md:pb-20 px-5 sm:px-8 md:px-10 lg:px-14 text-white">
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse at top right, rgba(255,255,255,0.15), transparent 55%)" }} />
        <div className="pointer-events-none absolute -right-32 -top-32 w-[500px] h-[500px] rounded-full bg-white/10 blur-3xl" />
        <div className="relative mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center" style={{ maxWidth: "1480px" }}>
          {/* LEFT */}
          <div className="lg:col-span-6">
            <nav aria-label="breadcrumb" className="text-xs md:text-sm text-white/70 mb-5">
              <Link to="/" className="hover:text-white">{u.breadcrumbHome}</Link>
              <span className="mx-2">/</span>
              <Link to="/" className="hover:text-white">{u.breadcrumbServices}</Link>
              <span className="mx-2">/</span>
              <span className="text-[#FFD84D] font-semibold">{serviceName}</span>
            </nav>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 px-3 py-1 text-[11px] uppercase tracking-[0.22em] text-[#FFD84D] font-bold mb-5">
              <Sparkles size={12} /> {content.badge}
            </span>
            <h1 className="font-extrabold text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.02]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {(lang === "fr" ? service.h1 : (lang === "de" ? `${serviceName} in der Westschweiz` : `${serviceName} in French-speaking Switzerland`))}.
            </h1>
            <p className="mt-5 text-white/85 text-base md:text-lg leading-relaxed max-w-xl">
              {content.promise}
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <button onClick={() => navigate(`/devis?service=${service.key}`)} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FFD84D] hover:bg-yellow-300 text-[#0A0F2C] px-7 py-4 font-extrabold text-sm md:text-base transition-all shadow-[0_15px_30px_-10px_rgba(255,216,77,0.55)]" data-testid="service-cta-quote">
                {lang === "de" ? "Kostenloses Angebot anfordern" : lang === "en" ? "Get a free quote" : "Demander un devis gratuit"} <ArrowRight size={18} />
              </button>
              <a href={whatsappHref} target="_blank" rel="noopener noreferrer nofollow" className="inline-flex items-center justify-center gap-2 rounded-full bg-white/10 hover:bg-white/15 border border-white/20 text-white px-7 py-4 font-semibold text-sm md:text-base transition-all" data-testid="service-cta-whatsapp">
                <MessageCircle size={18} /> {lang === "de" ? "Fotos via WhatsApp senden" : lang === "en" ? "Send my photos on WhatsApp" : "Envoyer mes photos sur WhatsApp"}
              </a>
            </div>
            <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-[13px] text-white/85">
              {(HERO_BADGES_I18N[lang] || HERO_BADGES_I18N.fr).map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5"><Check size={14} className="text-[#FFD84D]" strokeWidth={3} /> {t}</span>
              ))}
            </div>
          </div>
          {/* RIGHT — image + floating card */}
          <div className="lg:col-span-6 relative">
            <div className="relative aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl">
              <img src={heroImg} alt={content.heroAlt} className="absolute inset-0 w-full h-full object-cover" loading="eager" />
              <div className="absolute inset-0 bg-gradient-to-tr from-black/20 via-transparent to-transparent" />
            </div>
            {/* Floating mini card */}
            <div className="absolute bottom-4 left-4 sm:bottom-6 sm:left-6 bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-4 sm:p-5 max-w-[300px]">
              <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#2D5BFF] font-bold mb-2">
                <Sparkles size={12} /> {lang === "de" ? "Mopper Service" : lang === "en" ? "Mopper service" : "Service Mopper"}
              </div>
              <div className="text-sm font-extrabold text-[#0A0F2C] leading-tight">{x.sp.reviewsBadgeTitle}</div>
              <div className="text-xs text-[#4B5563] mt-1">{u.fastIntervention}</div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== 2. TRUST BAR ============== */}
      <section className="border-y border-gray-100 bg-[#F7F7F8] py-6 px-5 sm:px-8 md:px-10 lg:px-14">
        <div className="mx-auto flex flex-wrap items-center justify-center gap-x-7 gap-y-3 text-[13px] text-[#4B5563]" style={{ maxWidth: "1480px" }}>
          {(TRUST_BAR_I18N[lang] || TRUST_BAR_I18N.fr).map((t) => (
            <span key={t} className="inline-flex items-center gap-1.5 whitespace-nowrap"><Check size={14} className="text-[#2D5BFF]" strokeWidth={3} /> <span className="font-semibold">{t}</span></span>
          ))}
        </div>
      </section>

      {/* ============== 3. CE QUE MOPPER PREND EN CHARGE ============== */}
      <section className="py-16 md:py-24 px-5 sm:px-8 md:px-10 lg:px-14 bg-white">
        <div className="mx-auto" style={{ maxWidth: "1480px" }}>
          <div className="max-w-3xl">
            <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#2D5BFF]">{u.kicker.included}</span>
            <h2 className="font-extrabold text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05] mt-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {lang === "de" ? (<>Eine <span className="text-[#2D5BFF]">vollständige</span> Leistung, ohne Überraschungen.</>)
               : lang === "en" ? (<>A <span className="text-[#2D5BFF]">complete</span> service, no surprises.</>)
               : (<>Une prestation <span className="text-[#2D5BFF]">complète</span>, sans surprise.</>)}
            </h2>
            <p className="mt-4 text-[#4B5563] text-base md:text-lg">{u.includesIntro(serviceName)}</p>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {content.includes.map((item, i) => {
              const Ic = ICONS[item.icon] || PackageCheck;
              return (
                <div key={i} className="bg-[#F7F7F8] rounded-2xl p-6 hover:bg-[#EAF0FF] transition-colors" data-testid={`includes-${i}`}>
                  <div className="w-11 h-11 rounded-xl bg-white text-[#2D5BFF] inline-flex items-center justify-center shadow-sm"><Ic size={20} /></div>
                  <h3 className="mt-4 font-extrabold text-lg text-[#0A0F2C]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{item.title}</h3>
                  <p className="mt-1.5 text-[14px] text-[#4B5563] leading-relaxed">{item.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============== 4. COMMENT ÇA MARCHE ============== */}
      <section className="py-16 md:py-24 px-5 sm:px-8 md:px-10 lg:px-14 bg-[#F7F7F8]">
        <div className="mx-auto" style={{ maxWidth: "1480px" }}>
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#2D5BFF]">{u.kicker.how}</span>
            <h2 className="font-extrabold text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05] mt-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {lang === "de" ? "4 einfache Schritte, null Stress." : lang === "en" ? "4 simple steps, zero stress." : x.sp.step4Title}
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {(lang === "de" ? [
              { n: "01", t: "Sie erklären uns", d: "Füllen Sie das Formular aus, rufen Sie an oder senden Sie Fotos via WhatsApp." },
              { n: "02", t: "Wir bewerten", d: "Wir analysieren Volumen, Zugang, Optionen und spezifische Bedürfnisse." },
              { n: "03", t: "Sie erhalten das Angebot", d: "Klares, kostenloses, unverbindliches Angebot innert 24 Stunden." },
              { n: "04", t: "Mopper kümmert sich um alles", d: "Das Team rückt mit passendem Material und Fahrzeugen aus." },
            ] : lang === "en" ? [
              { n: "01", t: "You tell us", d: "Fill in the form, call us or send your photos on WhatsApp." },
              { n: "02", t: "We assess", d: "We analyze volume, access, options and specific needs." },
              { n: "03", t: "You get the quote", d: "Clear, free, no-commitment quote within 24 hours." },
              { n: "04", t: "Mopper handles everything", d: "The team arrives with suitable equipment and vehicles." },
            ] : [
              { n: "01", t: "Vous nous expliquez", d: "Remplissez le formulaire, appelez-nous ou envoyez vos photos par WhatsApp." },
              { n: "02", t: "Nous évaluons", d: "On analyse volume, accès, options et besoins spécifiques." },
              { n: "03", t: "Vous recevez le devis", d: "Devis clair, gratuit, sans engagement, sous 24 heures." },
              { n: "04", t: "Mopper s'occupe de tout", d: "L'équipe intervient avec le matériel et les véhicules adaptés." },
            ]).map((s, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 md:p-7 relative shadow-sm">
                <div className="absolute -top-3 -right-3 w-12 h-12 rounded-full bg-[#FFD84D] flex items-center justify-center font-extrabold text-[#0A0F2C] text-sm">{s.n}</div>
                <h3 className="font-extrabold text-lg md:text-xl text-[#0A0F2C] pr-10" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{s.t}</h3>
                <p className="mt-2 text-[14px] text-[#4B5563] leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== 5. UNE SOLUTION POUR CHAQUE BESOIN ============== */}
      <section className="py-16 md:py-24 px-5 sm:px-8 md:px-10 lg:px-14 bg-white">
        <div className="mx-auto" style={{ maxWidth: "1480px" }}>
          <div className="max-w-3xl">
            <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#2D5BFF]">{u.kicker.solutions}</span>
            <h2 className="font-extrabold text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05] mt-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {lang === "de" ? "Erkennen Sie sich in einer konkreten Situation." : lang === "en" ? "Recognize yourself in a real situation." : "Retrouvez-vous dans une situation concrète."}
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {service.useCases.map((c, i) => (
              <div key={i} className="group bg-white border border-gray-100 rounded-2xl p-6 hover:border-[#2D5BFF] hover:shadow-lg transition-all" data-testid={`usecase-${i}`}>
                <div className="w-10 h-10 rounded-xl bg-[#FFF8DC] text-[#A88A00] inline-flex items-center justify-center"><Sparkles size={18} /></div>
                <h3 className="mt-4 font-extrabold text-base md:text-lg text-[#0A0F2C] leading-snug" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{c}</h3>
                <button onClick={() => navigate(`/devis?service=${service.key}`)} className="mt-4 inline-flex items-center gap-1.5 text-[#2D5BFF] hover:text-[#224DE5] text-sm font-bold group-hover:gap-2 transition-all">
                  {lang === "de" ? "Angebot anfordern" : lang === "en" ? "Get a quote" : "Demander un devis"} <ArrowRight size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== 6. RÉASSURANCE VISUELLE ============== */}
      <section className="py-16 md:py-24 px-5 sm:px-8 md:px-10 lg:px-14 bg-[#F7F7F8]">
        <div className="mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center" style={{ maxWidth: "1480px" }}>
          <div className="relative aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5] rounded-3xl overflow-hidden order-2 lg:order-1">
            <img src={REASSURANCE_IMG} alt={`Mopper ${lang === "de" ? "im Einsatz" : lang === "en" ? "intervention" : "en intervention"} ${serviceName.toLowerCase()}`} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/55 to-transparent p-6 sm:p-7">
              <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#FFD84D]">{u.kicker.field}</span>
              <div className="text-white font-extrabold text-xl md:text-2xl mt-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{lang === "de" ? "Ein Team, ein Ziel: Ihre Ruhe." : lang === "en" ? "One team, one goal: your peace of mind." : "Une équipe, un objectif : votre tranquillité."}</div>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#2D5BFF]">{u.kicker.quality}</span>
            <h2 className="font-extrabold text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05] mt-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {content.reassuranceTitle}
            </h2>
            <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-5">
              {content.reassuranceItems.map((it, i) => (
                <li key={i} className="flex items-start gap-2.5 text-[15px]"><span className="mt-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#2D5BFF] text-white shrink-0"><Check size={11} strokeWidth={3} /></span><span>{it}</span></li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ============== 7. NIVEAUX D'ACCOMPAGNEMENT ============== */}
      <section className="py-16 md:py-24 px-5 sm:px-8 md:px-10 lg:px-14 bg-white">
        <div className="mx-auto" style={{ maxWidth: "1480px" }}>
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#2D5BFF]">{u.kicker.tiers}</span>
            <h2 className="font-extrabold text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05] mt-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {lang === "de" ? "Wählen Sie das passende Paket." : lang === "en" ? "Choose the level that fits you." : "Choisissez le niveau qui vous convient."}
            </h2>
            <p className="mt-4 text-[#4B5563]">{u.tiersIntro}</p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {content.tiers.map((tier, i) => (
              <div key={i} className={`relative rounded-3xl p-7 md:p-8 ${tier.highlight ? "bg-[#0A0F2C] text-white shadow-2xl scale-[1.02]" : "bg-[#F7F7F8] text-[#0A0F2C]"}`} data-testid={`tier-${i}`}>
                {tier.highlight && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FFD84D] text-[#0A0F2C] px-3 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider">{u.tiersBest}</span>
                )}
                <div className={`text-[11px] font-bold uppercase tracking-[0.22em] ${tier.highlight ? "text-[#FFD84D]" : "text-[#2D5BFF]"}`}>{tier.tagline}</div>
                <h3 className="font-extrabold text-2xl md:text-3xl mt-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{tier.name}</h3>
                <ul className="mt-6 flex flex-col gap-2.5">
                  {tier.items.map((it, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-[14.5px]"><Check size={16} className={`shrink-0 mt-0.5 ${tier.highlight ? "text-[#FFD84D]" : "text-[#2D5BFF]"}`} strokeWidth={3} /><span>{it}</span></li>
                  ))}
                </ul>
                <button onClick={() => navigate(`/devis?service=${service.key}`)} className={`mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full py-3.5 text-sm font-extrabold transition-all ${tier.highlight ? "bg-[#FFD84D] hover:bg-yellow-300 text-[#0A0F2C]" : "bg-white hover:bg-[#EAF0FF] text-[#0A0F2C] border border-gray-200"}`}>
                  {x.sp.tierCta} <ArrowRight size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== 7.bis FACTEURS DE PRIX (Phase 3A) ============== */}
      <section className="py-16 md:py-24 px-5 sm:px-8 md:px-10 lg:px-14 bg-[#F7F7F8]">
        <div className="mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14" style={{ maxWidth: "1280px" }}>
          <div className="lg:col-span-5">
            <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#2D5BFF]">{p3a.pricingKicker}</span>
            <h2 className="font-extrabold text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05] mt-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {p3a.pricingTitle}
            </h2>
            <p className="mt-4 text-[#4B5563] text-base md:text-lg leading-relaxed">{p3a.pricingSub}</p>
            <button
              onClick={() => navigate(`/devis?service=${service.key}`)}
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#0A0F2C] hover:bg-[#1a2050] text-white px-6 py-3 text-sm font-extrabold transition-all"
              data-testid="pricing-cta-quote"
            >
              {lang === "de" ? "Mein persönliches Angebot anfordern" : lang === "en" ? "Get my personalized quote" : "Recevoir mon devis personnalisé"}
              <ArrowRight size={14} />
            </button>
          </div>
          <div className="lg:col-span-7">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
              {pricingFactors.map((f, i) => (
                <li key={i} className="bg-white rounded-2xl p-5 border border-gray-100 flex items-start gap-3" data-testid={`pricing-factor-${i}`}>
                  <span className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-xl bg-[#EAF0FF] text-[#2D5BFF] font-extrabold text-sm">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-[14.5px] text-[#0A0F2C] leading-relaxed font-medium">{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ============== 8. INTERVENTION RÉALISÉE (CASE STUDY) ============== */}
      <section className="py-16 md:py-24 px-5 sm:px-8 md:px-10 lg:px-14 bg-[#F7F7F8]">
        <div className="mx-auto bg-white rounded-3xl overflow-hidden shadow-sm grid grid-cols-1 lg:grid-cols-5" style={{ maxWidth: "1280px" }}>
          <div className="lg:col-span-2 relative aspect-[5/4] lg:aspect-auto min-h-[280px]">
            <img src={heroImg} alt={`Exemple intervention ${service.name.toLowerCase()}`} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
          </div>
          <div className="lg:col-span-3 p-7 md:p-10 lg:p-12">
            <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#2D5BFF]">{u.kicker.case}</span>
            <h2 className="font-extrabold text-2xl md:text-3xl lg:text-4xl tracking-tight leading-[1.1] mt-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {content.caseStudy.title}
            </h2>
            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {content.caseStudy.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2.5 text-[15px]"><ClipboardList size={16} className="text-[#2D5BFF] shrink-0 mt-1" /><span>{b}</span></li>
              ))}
            </ul>
            <button onClick={() => navigate(`/devis?service=${service.key}`)} className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#2D5BFF] hover:bg-[#224DE5] text-white px-6 py-3 text-sm font-extrabold transition-all">
              {x.sp.caseCta} <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </section>

      {/* ============== 9. AVIS CLIENTS ============== */}
      <section className="py-16 md:py-24 px-5 sm:px-8 md:px-10 lg:px-14 bg-white">
        <div className="mx-auto" style={{ maxWidth: "1480px" }}>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div>
              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#2D5BFF]">{u.kicker.reviews}</span>
              <h2 className="font-extrabold text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05] mt-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {x.sp.reviewsTitle}
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {content.testimonials.map((t, i) => (
              <article key={i} className="bg-[#F7F7F8] rounded-2xl p-6 md:p-7 flex flex-col" data-testid={`testimonial-${i}`}>
                <div className="flex gap-0.5 mb-3">
                  {[...Array(t.rating)].map((_, j) => <Star key={j} size={14} className="fill-[#FFD84D] text-[#FFD84D]" />)}
                </div>
                <p className="text-[15px] text-[#0A0F2C] leading-relaxed flex-1">« {t.text} »</p>
                <div className="mt-5 pt-5 border-t border-gray-200">
                  <div className="font-extrabold text-sm text-[#0A0F2C]">{t.name} · {t.city}</div>
                  <div className="text-[12px] text-[#4B5563] mt-0.5">{t.type}</div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ============== 10. ZONES D'INTERVENTION ============== */}
      <section className="py-16 md:py-24 px-5 sm:px-8 md:px-10 lg:px-14 bg-[#F7F7F8]">
        <div className="mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10" style={{ maxWidth: "1480px" }}>
          <div className="lg:col-span-7">
            <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#2D5BFF]">{u.kicker.zones}</span>
            <h2 className="font-extrabold text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05] mt-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {x.sp.zonesTitle}
            </h2>
            <p className="mt-4 text-[#4B5563] max-w-xl">{u.zonesIntro}</p>
            <div className="mt-7 flex flex-wrap gap-2.5">
              {CANTONS.map((c) => (
                <Link key={c.slug} to={`/zones/${c.slug}`} className="inline-flex items-center gap-1.5 rounded-full bg-white hover:bg-[#2D5BFF] hover:text-white text-[#0A0F2C] px-4 py-2 text-sm font-semibold transition-colors border border-gray-200" data-testid={`service-zone-${c.slug}`}>
                  <MapPin size={13} /> {c.name}
                </Link>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 bg-white rounded-3xl p-7 md:p-8 shadow-sm">
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#2D5BFF] mb-2">{u.zonesQuickCheck}</div>
            <h3 className="font-extrabold text-xl md:text-2xl text-[#0A0F2C]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{x.sp.zonesCheckTitle}</h3>
            <form onSubmit={checkZip} className="mt-5 flex flex-col sm:flex-row gap-3">
              <input value={zip} onChange={(e) => { setZip(e.target.value); setZipFeedback(null); }} type="text" inputMode="numeric" placeholder={x.sp.zonesPlaceholder} maxLength={4} className="flex-1 px-4 py-3 rounded-full border border-gray-200 focus:border-[#2D5BFF] focus:outline-none text-sm" data-testid="zip-input" />
              <button type="submit" className="rounded-full bg-[#2D5BFF] hover:bg-[#224DE5] text-white px-6 py-3 text-sm font-extrabold transition-colors whitespace-nowrap" data-testid="zip-check">
                {x.common.checkVerify}
              </button>
            </form>
            {zipFeedback && (
              <div className={`mt-4 p-3 rounded-xl text-sm ${zipFeedback.ok ? "bg-[#EAF0FF] text-[#0A0F2C]" : "bg-yellow-50 text-[#A88A00]"}`} data-testid="zip-result">
                {zipFeedback.msg}
              </div>
            )}
            <div className="mt-5 text-[12px] text-[#9CA3AF]">{u.zonesOutside}</div>
          </div>
        </div>
      </section>

      {/* ============== 10.ter PAGES LOCALES (service × ville) ============== */}
      {(localCities.length > 0 || tarifSlug) && (
        <section className="py-14 md:py-20 px-5 sm:px-8 md:px-10 lg:px-14 bg-white">
          <div className="mx-auto" style={{ maxWidth: "1480px" }}>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#2D5BFF]">{lang === "de" ? "Städte" : lang === "en" ? "Cities" : "Villes"}</span>
                <h2 className="font-extrabold text-3xl md:text-4xl tracking-tight leading-[1.05] mt-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {serviceName} {lang === "de" ? "in Ihrer Nähe" : lang === "en" ? "near you" : "près de chez vous"}
                </h2>
              </div>
              {tarifSlug && (
                <Link to={`/tarifs/${tarifSlug}`} className="inline-flex items-center gap-2 rounded-full bg-[#F7F7F8] hover:bg-[#EAF0FF] text-[#0A0F2C] px-5 py-3 text-sm font-bold transition-colors" data-testid="service-tarif-link">
                  <Tag size={15} /> {lang === "de" ? "Preise ansehen" : lang === "en" ? "See prices" : "Voir les tarifs"} <ArrowRight size={14} />
                </Link>
              )}
            </div>
            {localCities.length > 0 && (
              <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                {localCities.map((p) => (
                  <Link key={p.citySlug} to={p.path} className="group bg-[#F7F7F8] hover:bg-[#EAF0FF] rounded-2xl p-5 transition-colors" data-testid={`service-local-${p.citySlug}`}>
                    <div className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-white text-[#2D5BFF] shadow-sm"><MapPin size={16} /></div>
                    <div className="mt-3 font-extrabold text-[#0A0F2C] text-base group-hover:text-[#2D5BFF] transition-colors" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{serviceName} {lang === "de" || lang === "en" ? "in" : "à"} {p.cityName}</div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ============== 10.bis ENGAGEMENTS MOPPER (Phase 3A) ============== */}
      <section className="py-16 md:py-24 px-5 sm:px-8 md:px-10 lg:px-14 bg-white">
        <div className="mx-auto" style={{ maxWidth: "1280px" }}>
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#2D5BFF]">{p3a.commitmentsKicker}</span>
            <h2 className="font-extrabold text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05] mt-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {p3a.commitmentsTitle}
            </h2>
            <p className="mt-4 text-[#4B5563] text-base md:text-lg">{p3a.commitmentsSub}</p>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {commitments.map((c, i) => {
              const Ic = ICONS[c.icon] || ShieldCheck;
              return (
                <div key={i} className="group bg-[#F7F7F8] hover:bg-[#EAF0FF] rounded-2xl p-6 transition-colors" data-testid={`commitment-${i}`}>
                  <div className="w-11 h-11 rounded-xl bg-white text-[#2D5BFF] inline-flex items-center justify-center shadow-sm"><Ic size={20} /></div>
                  <h3 className="mt-4 font-extrabold text-base md:text-lg text-[#0A0F2C] leading-snug" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{c.title}</h3>
                  <p className="mt-1.5 text-[14px] text-[#4B5563] leading-relaxed">{c.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============== 11. FAQ ============== */}
      <section className="py-16 md:py-24 px-5 sm:px-8 md:px-10 lg:px-14 bg-white">
        <div className="mx-auto" style={{ maxWidth: "900px" }}>
          <div className="text-center">
            <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#2D5BFF]">{u.kicker.faq}</span>
            <h2 className="font-extrabold text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05] mt-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{x.sp.faqTitle}</h2>
          </div>
          <div className="mt-10 flex flex-col gap-3">
            {faqs.map((it, i) => {
              const open = openIdx === i;
              return (
                <div key={i} className="bg-[#F7F7F8] rounded-2xl overflow-hidden" data-testid={`faq-item-${i}`}>
                  <button className="w-full text-left p-5 md:p-6 flex items-center justify-between gap-4" onClick={() => setOpenIdx(open ? -1 : i)} aria-expanded={open}>
                    <span className="font-bold text-[#0A0F2C] text-base md:text-lg">{it.q}</span>
                    <ChevronDown className={`shrink-0 text-[#2D5BFF] transition-transform ${open ? "rotate-180" : ""}`} size={20} />
                  </button>
                  {open && <div className="px-5 md:px-6 pb-5 md:pb-6 text-[#4B5563] leading-relaxed">{it.a}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============== 12.bis CROSS-LINK SERVICES ============== */}
      {content.related && content.related.length > 0 && (
        <section className="pb-12 md:pb-16 px-5 sm:px-8 md:px-10 lg:px-14 bg-white">
          <div className="mx-auto" style={{ maxWidth: "1280px" }}>
            <div className="border-t border-gray-100 pt-12">
              <h2 className="font-extrabold text-2xl md:text-3xl tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{x.sp.relatedTitle}</h2>
              <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                {content.related.filter((rk) => !HIDDEN_SERVICE_KEYS.includes(rk)).map((rk) => {
                  const rService = SERVICES.find((s) => s.key === rk);
                  if (!rService) return null;
                  return (
                    <Link key={rk} to={`/services/${rService.slug}`} className="group bg-[#F7F7F8] hover:bg-[#EAF0FF] rounded-2xl p-5 transition-colors" data-testid={`related-${rk}`}>
                      <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#9CA3AF]">{u.serviceLabel}</div>
                      <div className="mt-1 font-extrabold text-[#0A0F2C] text-base md:text-lg group-hover:text-[#2D5BFF] transition-colors" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{SERVICE_LABELS[rk]}</div>
                      <div className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#2D5BFF]">{u.discover} <ArrowRight size={12} /></div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ============== 12. FINAL CTA ============== */}
      <section className="relative py-16 md:py-24 px-5 sm:px-8 md:px-10 lg:px-14 bg-[#0A0F2C] text-white overflow-hidden">
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse at top right, rgba(45,91,255,0.35), transparent 55%)" }} />
        <div className="relative mx-auto text-center" style={{ maxWidth: "900px" }}>
          <h2 className="font-extrabold text-3xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.98]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {x.sp.finalH2a} <span className="text-[#FFD84D]">{serviceName.toLowerCase()}</span> {x.sp.finalH2b}
          </h2>
          <p className="mt-6 text-white/85 text-base md:text-lg max-w-2xl mx-auto">
            {x.sp.finalSub}
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => navigate(`/devis?service=${service.key}`)} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FFD84D] hover:bg-yellow-300 text-[#0A0F2C] px-7 py-4 font-extrabold text-sm md:text-base transition-all shadow-[0_15px_30px_-10px_rgba(255,216,77,0.55)]">
              {x.common.requestQuoteMine} <ArrowRight size={18} />
            </button>
            <a href={whatsappHref} target="_blank" rel="noopener noreferrer nofollow" className="inline-flex items-center justify-center gap-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white px-7 py-4 font-semibold text-sm md:text-base transition-all">
              <MessageCircle size={18} /> {x.common.whatsapp}
            </a>
            <a href="tel:+41782321850" className="inline-flex items-center justify-center gap-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white px-7 py-4 font-semibold text-sm md:text-base transition-all">
              <Phone size={18} /> {x.common.callBack}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
